import { useCallback, useContext, useMemo } from "react";

import { useModalDialog } from "../../../../components/ui";
import { DispatchContext } from "../../../../context/domain/planning/DispatchContext";
import { WeeklyContext } from "../../../../context/global/WeeklyContext";
import { deleteWorkScopeAspects } from "../../../../services/api/dispatch";
import { formatDate } from "../../../../utils/datetime";

/**
 * Build Dispatch WorkScopeAspect action menu items, modal state, and mutation handlers.
 *
 * @param {Object} params
 * @param {Object} params.scope Dispatch WorkScopeAspect row.
 * @param {boolean} [params.disableCopy=false] Whether copy actions should be omitted.
 * @returns {Object}
 */
export default function useDispatchScopeActions({ scope, disableCopy = false }) {
  const {
    copyScopeOrigin,
    setCopyScopeOrigin,
    setScopesToDelete,
    toggleScopeSkipped,
    updateScope,
    runPlanningOperation,
  } = useContext(DispatchContext);
  const { reloadScopes } = useContext(WeeklyContext);
  const [
    isCustomAddressModalOpen,
    onCustomAddressModalOpen,
    onCustomAddressModalClose,
  ] = useModalDialog();
  const [isCustomTimeModalOpen, onCustomTimeModalOpen, onCustomTimeModalClose] =
    useModalDialog();
  const [
    isConfirmDeleteModalOpen,
    onConfirmDeleteModalOpen,
    onConfirmDeleteModalClose,
  ] = useModalDialog();

  const handleSaveCustomAddress = useCallback(
    async (e) => {
      e.preventDefault();
      if (scope.id) {
        await updateScope(scope.id, new FormData(e.currentTarget));
        reloadScopes();
      }
      onCustomAddressModalClose();
    },
    [onCustomAddressModalClose, reloadScopes, scope.id, updateScope]
  );

  const handleSaveCustomTime = useCallback(
    async (e) => {
      e.preventDefault();
      if (scope.id) {
        await updateScope(scope.id, new FormData(e.currentTarget));
        reloadScopes();
      }
      onCustomTimeModalClose();
    },
    [onCustomTimeModalClose, reloadScopes, scope.id, updateScope]
  );

  const handleDeleteScope = useCallback(async () => {
    setScopesToDelete([]);
    await runPlanningOperation({
      module: "dispatch",
      operation: "deleteWorkScopeAspect",
      resources: ["dispatch.aspects"],
      entityIds: [scope.id],
      apiCall: () => deleteWorkScopeAspects([scope.id]),
    });
    onConfirmDeleteModalClose();
    reloadScopes();
  }, [
    onConfirmDeleteModalClose,
    reloadScopes,
    runPlanningOperation,
    scope.id,
    setScopesToDelete,
  ]);

  const menuItems = useMemo(() => {
    const copyOriginMatch = copyScopeOrigin ? copyScopeOrigin[0] === scope.id : false;
    const copyScopeText = copyOriginMatch
      ? "Cancel Copy Work Scope (Esc)"
      : "Copy Work Scope";
    const copyScopeAction = copyOriginMatch
      ? () => setCopyScopeOrigin([])
      : () => setCopyScopeOrigin([scope.id]);

    return {
      ...(disableCopy ? {} : { [copyScopeText]: copyScopeAction }),
      "Set Custom Address": () => onCustomAddressModalOpen(),
      "Set Custom Start Time": () => onCustomTimeModalOpen(),
      [scope.skipped ? "Remove Skipped" : "Set as Skipped"]: () =>
        toggleScopeSkipped(scope),
      "Delete work scope": () => {
        setScopesToDelete([scope.id]);
        onConfirmDeleteModalOpen();
      },
    };
  }, [
    copyScopeOrigin,
    disableCopy,
    onConfirmDeleteModalOpen,
    onCustomAddressModalOpen,
    onCustomTimeModalOpen,
    scope,
    setCopyScopeOrigin,
    setScopesToDelete,
    toggleScopeSkipped,
  ]);

  return {
    menuItems,
    customAddressModal: {
      scope,
      open: isCustomAddressModalOpen,
      onClose: onCustomAddressModalClose,
      onSubmit: handleSaveCustomAddress,
    },
    customTimeModal: {
      scope,
      open: isCustomTimeModalOpen,
      onClose: onCustomTimeModalClose,
      onSubmit: handleSaveCustomTime,
    },
    deleteModal: {
      isOpen: isConfirmDeleteModalOpen,
      title: "Confirm Delete Scope",
      list: [
        scope.scope?.title,
        `${scope.job.number} - ${scope.job.description}`,
        formatDate(scope.date, "MM/DD/YYYY"),
      ],
      text: "Are you sure you want to delete this scope?",
      buttonText: "Delete",
      onConfirm: handleDeleteScope,
      onClose: () => {
        onConfirmDeleteModalClose();
        setScopesToDelete([]);
      },
    },
  };
}
